import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

export default function ReflectMint() {
  const passes = [
    {
      name: "Common Pass",
      image: "/assets/images/common.png",
      description: "Mint memories up to 2 years old."
    },
    {
      name: "Rare Pass",
      image: "/assets/images/rare.png",
      description: "Mint memories up to 10 years old."
    },
    {
      name: "Ultra-Rare Pass",
      image: "/assets/images/ultra-rare.png",
      description: "Mint memories up to 20 years old."
    },
    {
      name: "Legendary Pass",
      image: "/assets/images/legendary.png",
      description: "Mint memories from any era — including historical moments."
    }
  ];

  const mintUrl = "https://launchmynft.io/collections/6AUXuSg2MBwPq5QPYyGqivZjzgn6GZJ3iJ4o5r3wD6ie/JXSFUj0MUR1Gd0T96jhY";
  const seoDescription = "Reflect is a revolutionary simulation that lets you mint your memories as interactive worlds. Relive, rewrite, and preserve your most important moments through memory NFTs, AI Reflection Avatars, and Quantum Jumping technology.";
  const seoImage = "https://selflabs.org/assets/images/reflect.png";

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <Helmet>
        <title>Reflect: Relive Your Memories in the Metaverse | SELF Labs</title>
        <meta name="description" content={seoDescription} />
        
        {/* OpenGraph tags */}
        <meta property="og:title" content="Reflect: Relive Your Memories in the Metaverse" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SELF Labs" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reflect: Relive Your Memories in the Metaverse" />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>

      {/* Floating memory fragments effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-full h-full">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-purple-400/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0.2, 0.8, 0.2],
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                ease: "linear",
                delay: Math.random() * 5,
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 text-transparent bg-clip-text">
            Reflect: Relive Your Memories in the Metaverse
          </h1>
          <h2 className="text-xl md:text-2xl text-gray-300 mb-6 font-light">
            Own Your Past. Rewrite Your Future.
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Reflect is a revolutionary simulation that lets you mint your memories as interactive worlds. 
            Relive, rewrite, and preserve your most important moments through memory NFTs, AI Reflection Avatars, 
            and Quantum Jumping technology.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className="mb-16 relative"
        >
          <div className="aspect-[16/9] overflow-hidden rounded-2xl 
                        shadow-[0_0_30px_rgba(168,85,247,0.2)]
                        hover:shadow-[0_0_40px_rgba(168,85,247,0.3)]
                        transition-all duration-300">
            <img 
              src="/assets/images/reflect.png"
              alt="Reflect: Relive Your Memories"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {passes.map((pass, index) => (
            <motion.div
              key={pass.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="bg-gradient-to-b from-purple-900/30 to-black rounded-2xl p-6 border border-purple-500/20 
                         backdrop-blur-sm hover:border-purple-500/50 transition-all duration-300
                         group hover:shadow-[0_0_20px_rgba(168,85,247,0.2)]"
            >
              <div className="aspect-square overflow-hidden rounded-xl mb-4 
                            bg-gradient-to-br from-purple-900/50 to-black
                            group-hover:shadow-[0_0_30px_rgba(168,85,247,0.3)]">
                <img 
                  src={pass.image} 
                  alt={pass.name}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <h3 className="text-xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">
                {pass.name}
              </h3>
              <p className="text-gray-400">{pass.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <motion.a
            href={mintUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-12 py-4 text-lg font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full 
                     shadow-[0_0_20px_rgba(168,85,247,0.5)] hover:shadow-[0_0_30px_rgba(168,85,247,0.7)] 
                     transition-all duration-300 transform hover:scale-105
                     border border-purple-400/30 backdrop-blur-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Mint Your Reflect Pass
          </motion.a>
        </div>
      </div>
    </div>
  );
} 